import React, { useState, useCallback, useRef } from "react";
import { UploadIcon, FileTextIcon, XIcon } from "lucide-react";
import { trackEvent } from "@util/mixpanel";
import { catchErrorLog } from "@util/sentry";
import { uploadTempCV } from "@service/cv";

// Define a maximum file size (5MB)
const MAX_FILE_SIZE = 5 * 1024 * 1024;

interface Props {
    onUploadComplete: () => void;
}

export function ResumeUploadV2({ onUploadComplete }: Props) {
    const [isDragging, setIsDragging] = useState(false);
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [uploadError, setUploadError] = useState("");
    const [isUploading, setIsUploading] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const validateFile = (file: File): string | null => {
        if (file.size > MAX_FILE_SIZE) {
            return "File size must be less than 5MB.";
        }
        return null;
    };

    const handleFileDrop = useCallback((file: File) => {
        const error = validateFile(file);
        if (error) {
            setUploadError(error);
            setResumeFile(null);
        } else {
            setResumeFile(file);
            setUploadError("");
        }
    }, []);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileDrop(files[0]);
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files === null || e.target.files.length === 0) {
            console.log("No file selected");
            return;
        }

        const file = e.target.files[0];
        const error = validateFile(file);
        if (error) {
            catchErrorLog(`File validation error: ${error}`);
            return;
        }

        setResumeFile(file);

        setIsUploading(true);
        uploadTempCV(file).then((uploadResponse) => {
            console.log("Upload response:", uploadResponse);
            if (uploadResponse.success) {
                trackEvent('reupload_cv', {
                    resume_uploaded: true
                })
                sessionStorage.setItem("__TEMP_CVID__", uploadResponse.data.filename);
                onUploadComplete();
            }
        }).catch((error) => {
            catchErrorLog(`Error during upload/registration: ${error}`);
        })
    };

    return (
        <div className="w-full flex flex-col items-center">
            <div
                className={`w-full h-[300px] md:h-[344px] flex justify-center align-middle items-center border-2 border-dashed rounded-lg p-6 md:p-12 text-center transition-colors
 ${isDragging
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : "border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-400"
                    }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {!resumeFile ? (
                    <div className="flex flex-col">
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            onChange={handleUpload}
                            accept=".pdf,.docx"
                        />
                        <div className="flex flex-col justify-center mb-4">
                            <div className="flex justify-center">
                                <div className="mb-4 p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                                    <UploadIcon className="h-4 w-4 md:h-8 md:w-8 text-blue-500" />
                                </div>
                            </div>
                            <h3 className="text-md md:text-xl font-medium text-gray-900 dark:text-white mb-2">
                                Upload your resume
                            </h3>
                            <p className="text-gray-500 dark:text-gray-400 text-center mb-6 text-sm md:text-md">
                                Drag and drop your resume file here, or click to browse.
                            </p>
                        </div>

                        <div className="flex flex-col items-center">
                            <button
                                onClick={handleUploadClick}
                                className="mb-2 px-3 md:px-6 py-1 md:py-2 text-sm md:text-md bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors"
                            >
                                Browse files
                            </button>
                            <p className="text-sm text-gray-500 mt-1">
                                Supported format: PDF &amp; DOCX only (max 5MB).
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4 flex-1">
                        <div className="mx-auto w-8 md:w-12 h-8 md:h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                            <FileTextIcon className="h-4 md:h-8 w-4 md:w-8 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                            <p className="text-sm font-medium">{resumeFile.name}</p>
                            <p className="text-xs text-gray-500 mt-1">
                                {(resumeFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                        </div>

                        {isUploading && (
                            <div className="text-amber-500 text-sm flex items-center justify-center mt-2">
                                <div className="animate-spin h-3 w-3 mr-1 border-2 border-amber-500 border-t-transparent rounded-full"></div>
                                Uploading resume...
                            </div>
                        )}
                    </div>
                )}
            </div>

            {uploadError && (
                <div className="text-red-500 text-sm mt-2 flex items-center">
                    <XIcon className="h-4 w-4 mr-1" />
                    {uploadError}
                </div>
            )}
        </div>
    );
}
